import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Zap, Crown } from 'lucide-react';
import { AppButton } from '../ui';
import apiClient from '../../utils/apiClient';
import { getCurrentUser } from '../../utils/auth';
import './LockedFeature.scss';

/**
 * Feature-key → human-readable label map.
 * Keep in sync with Plan model boolean fields.
 */
const FEATURE_LABELS = {
  quizAssessment: 'Quiz & Assessments',
  aiMock: 'AI Mock Interview',
  aiLearning: 'AI Learning',
  aiQuestionBank: 'AI Question Bank',
  feeManagement: 'Fee Management',
  enquiryManagement: 'Enquiry Management',
  interviews: 'Interviews',
  codingPractice: 'Coding Practice',
  selfPacedCourses: 'Self-Paced Learning',
  assignments: 'Assignments',
  customBranding: 'Custom Branding',
  customDomain: 'Custom Domain',
  apiAccess: 'API Access',
  enquiryFormBuilder: 'Enquiry Form Builder',
  zoomV2: 'Zoom Integration',
  recordings: 'Class Recordings',
  whatsapp: 'WhatsApp Notifications',
  platformHostedZoom: 'Platform Hosted Zoom',
};

const LockedFeaturePage = ({ featureKey, featureLabel }) => {
  const navigate = useNavigate();
  const user = getCurrentUser();
  const isAdmin = user?.usertype === 'superadmin' || user?.usertype === 'admin';
  const label = featureLabel || FEATURE_LABELS[featureKey] || featureKey;

  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!featureKey) return;
    apiClient.get(`/billing/plans-for-feature?feature=${encodeURIComponent(featureKey)}`)
      .then(res => setPlans(res.data?.plans || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [featureKey]);

  const formatPrice = (price) => {
    if (!price) return 'Free';
    return `₹${price.toLocaleString('en-IN')}`;
  };

  return (
    <div className="locked-feature-page">
      <div className="locked-feature-card">
        <div className="locked-feature-icon">
          <Lock size={32} />
        </div>
        <h2 className="locked-feature-title">
          {label} is locked
        </h2>
        <p className="locked-feature-description">
          This feature is not available on your current plan.
          {isAdmin
            ? ' Upgrade your plan to unlock it.'
            : ' Contact your institute administrator to upgrade the plan.'}
        </p>

        {isAdmin && plans.length > 0 && (
          <div className="locked-feature-plans">
            <p className="locked-feature-plans-heading">
              <Crown size={16} /> Available in these plans
            </p>
            <div className="locked-feature-plans-list">
              {plans.map(plan => (
                <div key={plan._id} className="locked-feature-plan-item">
                  <div className="locked-feature-plan-name">{plan.name}</div>
                  <div className="locked-feature-plan-price">
                    {formatPrice(plan.priceMonthly)}
                    {plan.priceMonthly > 0 && <span className="locked-feature-plan-period">/mo</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {loading && <div className="locked-feature-loading">Loading plans...</div>}

        <div className="locked-feature-actions">
          {isAdmin && (
            <AppButton
              variant="primary"
              onClick={() => navigate('/billing')}
            >
              <Zap size={16} /> Upgrade Plan
            </AppButton>
          )}
          <AppButton
            variant="light"
            onClick={() => navigate(-1)}
          >
            Go Back
          </AppButton>
        </div>
      </div>
    </div>
  );
};

export default LockedFeaturePage;
